import React, { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import Icon from "./Icon";
import { CategoryTag, CategoryTile, StatusTag } from "./ProposalRow";
import { formatDate, reviewNote, reviewSteps, splitPrerequisites, statusOf } from "./proposalsModel";
import "./proposals.css";

function Section({ title, children }) {
  return <section className="dw-s"><h3>{title}</h3>{children}</section>;
}

function Timeline({ sub }) {
  const note = reviewNote(sub);
  const status = statusOf(sub);
  if (status === "cancelled") {
    return (
      <ol className="tl">
        <li className="done"><span className="dot"><Icon n="check" s={11} /></span><div><b>Submitted</b><small>{formatDate(sub.submittedAt, true)}</small></div></li>
        <li className="done"><span className="dot"><Icon n="check" s={11} /></span><div><b>Withdrawn by you</b></div></li>
      </ol>
    );
  }
  const dates = { submitted: formatDate(sub.submittedAt, true), decision: sub.reviewedAt ? formatDate(sub.reviewedAt, true) : "" };
  return (
    <ol className="tl">
      {reviewSteps(sub).map((step) => (
        <li key={step.key} className={step.state}>
          <span className="dot">{step.state === "done" || step.state === "good" ? <Icon n="check" s={11} /> : step.state === "bad" ? <Icon n="x" s={11} /> : null}</span>
          <div>
            <b>{step.label}</b>
            {dates[step.key] && <small>{dates[step.key]}</small>}
            {step.key === "review" && step.state === "now" && <small>Typically reviewed in 3–5 business days</small>}
            {step.key === "decision" && note && step.state !== "todo" && <p>{note}</p>}
          </div>
        </li>
      ))}
    </ol>
  );
}

function Footer({ sub, confirming, setConfirming, onClose, onWithdraw, onRevise, onSchedule }) {
  const status = statusOf(sub);
  if (confirming) {
    return (
      <div className="cfw">
        <p>Withdraw this proposal? The coordinator will stop reviewing it.</p>
        <button type="button" className="btn btn-g btn-sm" onClick={() => setConfirming(false)}>Cancel</button>
        <button type="button" className="btn btn-p btn-sm" autoFocus onClick={() => onWithdraw(sub)}>Withdraw</button>
      </div>
    );
  }
  return (
    <>
      <span>Submitted {formatDate(sub.submittedAt)}</span>
      {status === "pending" && <button type="button" className="btn btn-dg btn-sm" onClick={() => setConfirming(true)}>Withdraw</button>}
      <button type="button" className="btn btn-o btn-sm" onClick={onClose}>Close</button>
      {status === "approved" && <button type="button" className="btn btn-s btn-sm" onClick={() => onSchedule(sub)}><Icon n="plus" s={12} />Schedule sessions</button>}
      {(status === "rejected" || status === "cancelled") && <button type="button" className="btn btn-p btn-sm" onClick={() => onRevise(sub)}>Revise and resubmit</button>}
    </>
  );
}

/** Proposal detail dialog: review timeline, overview, description, objectives and prerequisites. */
export default function ProposalDialog({ sub, onClose, onWithdraw, onRevise, onSchedule }) {
  const [confirming, setConfirming] = useState(false);
  useEffect(() => { setConfirming(false); }, [sub?.id]);
  if (!sub) return null;

  const status = statusOf(sub);
  const prereqs = splitPrerequisites(sub.prerequisites);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="proposal-dialog-title" PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden", m: 2 } }}>
      <div className="pp-root dw" data-testid="proposal-dialog">
        <div className="dw-h">
          <CategoryTile category={sub.category} />
          <div>
            <h2 id="proposal-dialog-title">{sub.title}</h2>
            <div className="ttl"><span className="mono">#{sub.id}</span><CategoryTag category={sub.category} /><StatusTag status={status} /></div>
          </div>
          <button type="button" className="ib" onClick={onClose} aria-label="Close"><Icon n="x" s={18} /></button>
        </div>
        <div className="dw-b">
          <Section title="Review status"><Timeline sub={sub} /></Section>
          <Section title="Overview">
            <div className="kvs">
              <div><small>Hours</small>{sub.totalHours || "—"}</div>
              <div><small>Category</small>{sub.category || "General"}</div>
              <div><small>Submitted</small>{formatDate(sub.submittedAt, true)}</div>
            </div>
          </Section>
          {sub.description && <Section title="Description"><p>{sub.description}</p></Section>}
          {sub.objectives && <Section title="Learning objectives"><p>{sub.objectives}</p></Section>}
          {prereqs.length > 0 && <Section title="Prerequisites"><div className="chips">{prereqs.map((p) => <span key={p}>{p}</span>)}</div></Section>}
        </div>
        <div className="dw-f">
          <Footer sub={sub} confirming={confirming} setConfirming={setConfirming} onClose={onClose}
            onWithdraw={onWithdraw} onRevise={onRevise} onSchedule={onSchedule} />
        </div>
      </div>
    </Dialog>
  );
}
